import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { ContentTemplate, Divider, SectionHeader, TitleHeader, Typography } from 'web-library';
import { makeStyles } from 'tss-react/mui';
import { Theme } from '@mui/material';
import { MutationStatus } from 'react-query';
import { UploadedFile } from 'web-library/models/UploadedFile';
import {
  ApplicationType,
  AttachedFileType,
  UploadDocumentType,
} from '../../../api/candidate/config/model';
import UploadAdditionalDocumentOneUploadSection from '../../../modules/examRegistration/components/nonNationalExam/UploadAdditionalDocumentOneUploadSection';
import NonNationalUploadButtons from '../../../modules/examRegistration/components/nonNationalExam/NonNationalUploadButtons';
import {
  IncompleteDetailsResponse,
  useGetIncompleteDetails,
} from '../../../api-refactored/non-national-exam-registration/useGetIncompleteDetails';
import { UploadedFileModel } from '../../../api-refactored/non-national-exam-registration/useGetUploadedFileInfo';
import { ParentParticularsLocation } from './AEIS/ParentParticulars';
import { usePostUploadPresignedUrl } from '../../../api-refactored/special-consideration/usePostUploadPresignedUrl';
import { PresignedUrlResponse } from '../../../models/PresignedUrl';
import { prepareFiles } from '../../../utils/UploadUtil';
import { FileUploadState } from '../../../models/File';
import RouteKey from '../../../routes/key';
import { usePostValidateAdditionalDocuments } from '../../../api-refactored/non-national-exam-registration/usePostValidateAdditionalDocuments';
import AttachedFiles from '../../../components/upload-documents/AttachedFiles';
import UploadAdditionalDocumentsRemarks from '../../../modules/examRegistration/components/nonNationalExam/UploadAdditionalDocumentsRemarks';
import { useGetApplicationFileStatus } from '../../../api-refactored/document/useGetApplicationFileStatus';
import { useGetPassportDisabled } from '../../../api-refactored/non-national-exam-registration/useGetPassportDisabled';

interface UploadSupportingDocumentsPageProps {
  isAEIS: boolean;
}

const { summary } = RouteKey.exam.child;

const useStyles = makeStyles()((theme) => ({
  uploadDocumentStatement: {
    marginBottom: theme.spacing(2),
    color: theme.color.text.textSecondary,
    fontSize: '1rem',
  },
  sectionTitle: {
    marginBottom: theme.spacing(4),
  },
}));

const createDraftFilePhoto = (
  supportingDocuments: UploadedFileModel[],
  passportPhoto: UploadedFileModel,
  setDraftFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>,
  setDraftPhoto: React.Dispatch<React.SetStateAction<UploadedFile[]>>,
  setSubmittedDocuments: React.Dispatch<React.SetStateAction<UploadedFile[]>>
) => {
  let files: UploadedFile[] = [];
  if (supportingDocuments) {
    const attachFiles = supportingDocuments.map((f: UploadedFileModel) => {
      const newFile = new File([new ArrayBuffer(f.fileSizeInBytes)], f.fileName);
      return {
        file: newFile,
        hasError: false,
        pending: false,
        uploaded: true,
        key: f.fileUuid,
      };
    });
    setDraftFiles(attachFiles);
    files = attachFiles;
  }
  if (passportPhoto) {
    const newPhoto = new File(
      [new ArrayBuffer(passportPhoto.fileSizeInBytes)],
      passportPhoto.fileName
    );
    const attachPhoto = [
      {
        file: newPhoto,
        hasError: false,
        pending: false,
        uploaded: true,
        key: passportPhoto.fileUuid,
      },
    ];
    setDraftPhoto(attachPhoto);
    files = files.concat(attachPhoto);
  }
  setSubmittedDocuments(files);
};

interface UseNonNationalAdditionalDocumentProps {
  isAEIS: boolean;
  applicationUuid: string;
  nextPageUrl?: string;
}

const useNonNationalAddtionalDocument = ({
  isAEIS,
  applicationUuid,
  nextPageUrl,
}: UseNonNationalAdditionalDocumentProps) => {
  const history = useHistory();
  const { state } = useLocation<ParentParticularsLocation>();
  const { t } = useTranslation();
  const [draftFiles, setDraftFiles] = useState<UploadedFile[]>([]);
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [photo, setPhoto] = useState<UploadedFile[]>([]);
  const [draftPhoto, setDraftPhoto] = useState<UploadedFile[]>([]);
  const [apiButtonStatus, setApiButtonStatus] = useState<MutationStatus>('idle');
  const [shouldSubmit, setShouldSubmit] = useState<boolean>(false);

  const [disableDeleteDocument, setDisableDeleteDocument] = useState<boolean>(false);
  const [disableDeletePhoto, setDisableDeletePhoto] = useState<boolean>(false);
  const [isPhotoRejected, setIsPhotoRejected] = useState<boolean>(false);
  const [photoRejectedErrorMessage, setPhotoRejectedErrorMessage] = useState<string>('');
  const [isFileRejected, setIsFileRejected] = useState(false);
  const [fileRejectedErrorMessage, setFileRejectedErrorMessage] = useState<string>('');
  const [isDisableApiButton, setIsDisableApiButton] = useState<boolean>(false);
  const [submittedDocuments, setSubmittedDocuments] = useState<UploadedFile[]>([]);
  const [remarks, setRemarks] = useState('');

  const [isFetchedDraft, setIsFetchedDraft] = useState<boolean>(false);

  const [timeoutRejectedErrorMessage, setTimeoutRejectedErrorMessage] = useState<string>('');

  const proceedToNextPage = () => {
    if (nextPageUrl) {
      history.push(nextPageUrl, state);
    }
  };

  useEffect(() => {
    if (photo.length && isPhotoRejected) {
      setPhotoRejectedErrorMessage('');
    }
  }, [photo.length, isPhotoRejected]);

  const getUploadFilePresignedUrlMutation = usePostUploadPresignedUrl({
    onSuccess: (res: PresignedUrlResponse[]) => {
      setApiButtonStatus('loading');
      const newFilesInfo: React.SetStateAction<UploadedFile[]> = [];
      files.forEach((_item: UploadedFile, index: number) => {
        const pendingFile = files[index];
        const fileData = res[index];
        pendingFile.presignedUrl = fileData.presignedUrl;
        pendingFile.key = fileData.key;
        newFilesInfo.push(pendingFile);
      });
      setFiles(newFilesInfo);
      setIsDisableApiButton(false);
      setShouldSubmit(true);
    },
    onError: (err) => {
      const { message } = err?.response?.data || {};
      setTimeoutRejectedErrorMessage(message || 'An unknown error occurred');
    },
  });

  const mutatePostValidateAdditionalDocuments = usePostValidateAdditionalDocuments({
    uuid: applicationUuid,
    onSuccess: () => proceedToNextPage(),
    onError: (err) => {
      if (err.response?.data.code === '1003') {
        setFiles([]);
        setPhoto([]);
        refetchIncompleteDetails().then(() => null);
        setPhotoRejectedErrorMessage(t('exam->>UploadDocument.IncorrectPhotoSize'));
      } else {
        setPhotoRejectedErrorMessage(err.response?.data.message!);
      }
      setApiButtonStatus('idle');
      setPhoto([]);
      setIsDisableApiButton(false);
      setDisableDeleteDocument(false);
      setDisableDeletePhoto(false);
      setIsPhotoRejected(true);
    },
  });

  useEffect(() => {
    const isUploading = files.some((file) => file.pending);
    const isFailed = files.some((file) => file.hasError);
    const fileKey = files.map((f: UploadedFile) => f.key);

    switch (true) {
      case isUploading:
        setIsDisableApiButton(true);
        setApiButtonStatus('loading');
        break;
      case isFailed:
        setApiButtonStatus('idle');
        setDisableDeleteDocument(false);
        setIsDisableApiButton(false);
        break;
      case ((files.length > 0 && !isUploading && !isFailed) || !files.length) && shouldSubmit:
        mutatePostValidateAdditionalDocuments.mutate({
          supportingDocumentsFileUuid: fileKey,
          passportPhotoFileUuid: photo.length ? photo[0].key : undefined,
        });
        setShouldSubmit(false);
        break;
      default:
        break;
    }
  }, [applicationUuid, photo, files, mutatePostValidateAdditionalDocuments, shouldSubmit]);

  const getUploadPhotoPresignedUrlMutation = usePostUploadPresignedUrl({
    onSuccess: (res: PresignedUrlResponse[]) => {
      const newPhotoInfo: React.SetStateAction<UploadedFile[]> = [];
      photo.forEach((_item: UploadedFile, index: number) => {
        const pendingPhoto = photo[index];
        const photoData = res[index];
        pendingPhoto.presignedUrl = photoData.presignedUrl;
        pendingPhoto.key = photoData.key;
        newPhotoInfo.push(pendingPhoto);
      });
      setApiButtonStatus('loading');
      setPhoto(newPhotoInfo);
    },
    onError: (err) => {
      const { message } = err?.response?.data || {};
      setTimeoutRejectedErrorMessage(message || 'An unknown error occurred');
    },
  });

  const { refetch: refetchIncompleteDetails } = useGetIncompleteDetails({
    uuid: applicationUuid,
    onSuccess: (data: IncompleteDetailsResponse) => {
      const { supportingDocuments, passportPhoto } = data;
      createDraftFilePhoto(
        supportingDocuments,
        passportPhoto,
        setDraftFiles,
        setDraftPhoto,
        setSubmittedDocuments
      );
      setIsFetchedDraft(true);
      setRemarks(data.remarks);
    },
  });

  useGetApplicationFileStatus({
    fileKey: photo[0]?.key as string,
    enabled:
      getUploadPhotoPresignedUrlMutation.status === 'success' &&
      mutatePostValidateAdditionalDocuments.status === 'success' &&
      !isPhotoRejected,
    refetchInterval: 1000000,
    onSuccess: (response) => {
      if (response.fileStatus === FileUploadState.SUCCESS) {
        setApiButtonStatus('loading');
        if (isAEIS && files.length) {
          const { filenames } = prepareFiles(files, setFiles);
          getUploadFilePresignedUrlMutation.mutate({
            uuid: applicationUuid,
            applicationType: ApplicationType.NON_NATIONAL_EXAM,
            filenames,
            uploadDocumentType: UploadDocumentType.SUPP_DOC,
          });
        } else {
          setIsDisableApiButton(false);
          setShouldSubmit(true);
        }
      }
      if (response.fileStatus === FileUploadState.FAILED) {
        setApiButtonStatus('idle');
        setDisableDeletePhoto(false);
        setIsDisableApiButton(false);
      }
    },
  });

  useEffect(() => {
    const allFileNames = [...files, ...draftFiles].map((f) => f.file.name);
    const hasDuplicate = new Set(allFileNames).size !== allFileNames.length;
    setIsFileRejected(hasDuplicate);
    setFileRejectedErrorMessage(
      hasDuplicate ? t('exam->>UploadDocument.DuplicateFileNames') : ''
    );
  }, [files, draftFiles, t]);

  return {
    submittedDocuments,
    remarks,
    apiButtonStatus,
    disableDeleteDocument,
    disableDeletePhoto,
    isPhotoRejected,
    isFileRejected,
    isDisableApiButton,
    files,
    photo,
    draftFiles,
    draftPhoto,
    photoRejectedErrorMessage,
    fileRejectedErrorMessage,
    setFiles,
    setPhoto,
    setDraftFiles,
    setDraftPhoto,
    mutatePostValidateAdditionalDocuments,
    refetchIncompleteDetails,
    setIsDisableApiButton,
    setApiButtonStatus,
    proceedToNextPage,
    getUploadPhotoPresignedUrlMutation,
    getUploadFilePresignedUrlMutation,
    isFetchedDraft,
    timeoutRejectedErrorMessage,
  };
};

const UploadAdditionalDocuments = ({ isAEIS }: UploadSupportingDocumentsPageProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { uuid: applicationUuid } = useParams<{ uuid: string }>();

  const {
    submittedDocuments,
    remarks,
    apiButtonStatus,
    isDisableApiButton,
    isPhotoRejected,
    isFileRejected,
    fileRejectedErrorMessage,
    files,
    setFiles,
    draftFiles,
    setDraftFiles,
    photo,
    setPhoto,
    draftPhoto,
    setDraftPhoto,
    photoRejectedErrorMessage,
    mutatePostValidateAdditionalDocuments,
    refetchIncompleteDetails,
    setIsDisableApiButton,
    setApiButtonStatus,
    proceedToNextPage,
    getUploadPhotoPresignedUrlMutation,
    getUploadFilePresignedUrlMutation,
    isFetchedDraft,
    timeoutRejectedErrorMessage,
  } = useNonNationalAddtionalDocument({
    isAEIS,
    applicationUuid,
    nextPageUrl: summary.route,
  });

  const [isPassportDisabled, setIsPassportDisabled] = useState<boolean>(false);
  const [isPassportDisabledFlag, setIsPassportDisabledFlag] = useState<boolean>(false);
  useGetPassportDisabled({
    onSuccess: (res) => {
      setIsPassportDisabled(true);
      console.log(res);
      setIsPassportDisabledFlag(res.valueOf());
    },
  });

  useEffect(() => {
    if (isAEIS) {
      if (isPassportDisabledFlag) {
        setIsDisableApiButton(!files.length);
      } else {
        setIsDisableApiButton(!(files.length || photo.length));
      }
    } else {
      setIsDisableApiButton(!photo.length);
    }
  }, [
    files,
    files.length,
    isAEIS,
    photo,
    photo.length,
    isPassportDisabledFlag,
    setIsDisableApiButton,
  ]);

  useEffect(() => {
    const isAllFilesUploaded = files.every((file) => file.uploaded);
    if (
      mutatePostValidateAdditionalDocuments.status === 'error' &&
      isAllFilesUploaded &&
      isPhotoRejected &&
      files.length > 0 &&
      photo.length === 1
    ) {
      setFiles([]);
      setPhoto([]);
      refetchIncompleteDetails().then(() => null);
    }
  }, [
    files,
    isPhotoRejected,
    photo.length,
    refetchIncompleteDetails,
    mutatePostValidateAdditionalDocuments,
    setFiles,
    setPhoto,
  ]);

  const handleConfirmUpload = () => {
    setApiButtonStatus('loading');
    if (
      (draftPhoto.length && draftFiles.length && files.length === 0) ||
      (!isAEIS && draftPhoto.length) ||
      (isAEIS &&
        draftFiles.length === 0 &&
        files.length === 0 &&
        draftPhoto.length === 0 &&
        photo.length === 0) ||
      (!isAEIS && draftPhoto.length === 0 && photo.length === 0)
    ) {
      proceedToNextPage();
      return;
    }
    setIsDisableApiButton(true);
    if (photo.length) {
      const { filenames: photonames } = prepareFiles(photo, setPhoto);
      getUploadPhotoPresignedUrlMutation.mutate({
        uuid: applicationUuid,
        applicationType: ApplicationType.NON_NATIONAL_EXAM,
        filenames: photonames,
        uploadDocumentType: UploadDocumentType.PASSPORT_PHOTO,
      });
    } else if (isAEIS && files.length) {
      const { filenames } = prepareFiles(files, setFiles);
      getUploadFilePresignedUrlMutation.mutate({
        uuid: applicationUuid,
        applicationType: ApplicationType.NON_NATIONAL_EXAM,
        filenames,
        uploadDocumentType: UploadDocumentType.SUPP_DOC,
      });
    }
  };

  return (
    <>
      <TitleHeader title={t('exam->>NonNationalExam.UploadAdditionalDocumentForApplication')} />
      <ContentTemplate>
        <SectionHeader title={t('exam->>NonNationalExam.UploadAdditionalDocument')} />
        <AttachedFiles
          headerText={t('exam->>NonNationalExam.SubmittedDocuments')}
          files={submittedDocuments}
          hideDelete
          setFiles={() => null}
          onUploadFileDelete={() => null}
          downloadableLink
          attachedFileType={AttachedFileType.APPLICATION_FILE}
        />
        {remarks && (
          <>
            <UploadAdditionalDocumentsRemarks remarks={remarks} />
            <Divider paddingTop />
          </>
        )}

        <Typography className={classes.sectionTitle} variant="h3">
          {t('exam->>UploadDocument.UploadNewSupportingDocuments')}
        </Typography>

        {isAEIS && (
          <UploadAdditionalDocumentOneUploadSection
            isAEIS={isAEIS}
            files={files}
            setFiles={setFiles}
            draftFiles={draftFiles}
            setDraftFiles={setDraftFiles}
            uploadDocumentType={UploadDocumentType.SUPP_DOC}
            isLoading={apiButtonStatus === 'loading'}
            noteContent={
              <>
                <Typography className={classes.uploadDocumentStatement} variant="body2">
                  {t('exam->>UploadDocument.UploadAdditionalDocumentsStatement1')}
                </Typography>
                <Typography className={classes.uploadDocumentStatement} variant="body2">
                  {t('exam->>UploadDocument.UploadAdditionalDocumentsStatement2')}
                </Typography>
              </>
            }
            isFetchedDraft={isFetchedDraft}
          />
        )}

        {isPassportDisabled && !isPassportDisabledFlag && (
          <UploadAdditionalDocumentOneUploadSection
            isAEIS={isAEIS}
            files={photo}
            setFiles={setPhoto}
            draftFiles={draftPhoto}
            setDraftFiles={setDraftPhoto}
            uploadDocumentType={UploadDocumentType.PASSPORT_PHOTO}
            isLoading={apiButtonStatus === 'loading'}
            sectionTitle={{
              text: isAEIS ? t('exam->>UploadDocument.myPassportTitle') : '',
              style: 'secondary',
            }}
            noteContent={
              isAEIS ? (
                <Typography className={classes.uploadDocumentStatement} variant="body2">
                  {t('exam->>UploadDocument.PassportSizePhotoNote')}
                </Typography>
              ) : (
                <>
                  <Typography className={classes.uploadDocumentStatement} variant="body2">
                    {t('exam->>UploadDocument.UploadAdditionalDocumentsStatement1')}
                  </Typography>
                  <Typography className={classes.uploadDocumentStatement} variant="body2">
                    {t('exam->>UploadDocument.UploadAdditionalDocumentsStatement2')}
                  </Typography>
                </>
              )
            }
            isFetchedDraft={isFetchedDraft}
          />
        )}
        <NonNationalUploadButtons
          isPhotoRejected={isPhotoRejected}
          isFileRejected={isFileRejected}
          fileRejectedErrorMessage={fileRejectedErrorMessage}
          apiButtonStatus={apiButtonStatus}
          isDisableApiButton={isDisableApiButton || isFileRejected}
          photoRejectedErrorMessage={photoRejectedErrorMessage}
          handleConfirmUpload={handleConfirmUpload}
          buttonCancelText={t('commom->>Cancel')}
          buttonSubmitText={t('exam->>ReviewExam.UpdateApplication')}
          timeoutRejectedErrorMessage={timeoutRejectedErrorMessage}
        />
      </ContentTemplate>
    </>
  );
};

export default UploadAdditionalDocuments;
